import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useStep } from "flemo";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { deleteWordProgress, deleteWordProgressBySet, upsertWordProgress } from "@/api/wordProgress";
import { useFavoriteWordsQuery, useFlashcardWordsQuery } from "@/hooks/queries";
import { useInterstitialAd } from "@/hooks/useInterstitialAd";
import { useSpeech } from "@/hooks/useSpeech";
import { trackEvent } from "@/lib/analytics";
import { Direction, FlashcardCategory, type FlashcardSessionSize, Language } from "@/lib/constant";
import { logger } from "@/lib/logger";
import { shuffleArray } from "@/lib/utils";
import { useAuthStore } from "@/store/authStore";
import { useSettingsStore } from "@/store/settingsStore";
import type { FlashcardSettings, FlashcardWord } from "@/types/common";

import {
  computeBadgeCounts,
  computeHeaderProgress,
  type FlashcardSwipeResult,
  splitFlashcardSession,
  summarizeSessionHistory,
} from "./flashcardProgress";
import { useAutoPlay } from "./useAutoPlay";

const DEFAULT_SETTINGS: FlashcardSettings = {
  shuffleCards: false,
  ttsEnabled: false,
  trackCategories: true,
  frontSide: Language.ENGLISH,
  sessionSize: 100,
};

/** `/flashcards/:id` 덱·스와이프·모달 스텝. `FlashcardPage`에서 한 번만 호출. */
export function useFlashcard() {
  const { id, openOptionsModal, openResultModal, openCompletedModal, restartFromScratch } =
    useParams<"/flashcards/:id">();
  const { popStep, pushStep } = useStep<"/flashcards/:id">();
  const { replace } = useNavigate();
  const queryClient = useQueryClient();
  const { speak } = useSpeech();
  const { maybeShow: maybeShowAd } = useInterstitialAd();
  const userId = useAuthStore(state => state.user?.id);
  const persistedSessionSize = useSettingsStore(state => state.flashcardSessionSize);
  const setPersistedSessionSize = useSettingsStore(state => state.setFlashcardSessionSize);

  const isFavoritesMode = id === "favorites";
  const sourceKey = isFavoritesMode ? `favorites:${userId ?? "guest"}` : `set:${id}`;

  const flashcardQuery = useFlashcardWordsQuery(isFavoritesMode ? undefined : id);
  const favoriteQuery = useFavoriteWordsQuery(isFavoritesMode ? userId : undefined);

  const flashcardData = flashcardQuery.data;
  const sourceWords = isFavoritesMode ? favoriteQuery.data : flashcardData?.words;
  const sourceDataUpdatedAt = isFavoritesMode ? favoriteQuery.dataUpdatedAt : flashcardQuery.dataUpdatedAt;
  const isPending = isFavoritesMode ? favoriteQuery.isPending : flashcardQuery.isPending;
  const isError = isFavoritesMode ? favoriteQuery.isError : flashcardQuery.isError;

  // --- 로컬 상태: 덱·진행
  const [words, setWords] = useState<FlashcardWord[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [history, setHistory] = useState<FlashcardSwipeResult[]>([]);
  const [initialWords, setInitialWords] = useState<FlashcardWord[] | null>(null);
  const [allWords, setAllWords] = useState<FlashcardWord[]>([]);
  const [remainingWords, setRemainingWords] = useState<FlashcardWord[]>([]);
  const [totalWordCount, setTotalWordCount] = useState(0);
  const [baseKnownCount, setBaseKnownCount] = useState(0);
  const [baseLearningCount, setBaseLearningCount] = useState(0);
  const [learningDeckMode, setLearningDeckMode] = useState(false);
  const [syncedSourceKey, setSyncedSourceKey] = useState<string | null>(null);
  const [syncedDataUpdatedAt, setSyncedDataUpdatedAt] = useState<number | null>(null);

  // --- 로컬 상태: 카드 UI·설정
  const [isFlipped, setIsFlipped] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState<Direction | null>(null);
  const [settings, setSettings] = useState<FlashcardSettings>(() => ({
    ...DEFAULT_SETTINGS,
    sessionSize: persistedSessionSize,
  }));
  const [activeSessionSize, setActiveSessionSize] = useState<FlashcardSessionSize>(persistedSessionSize);
  const [isAutoPlay, setIsAutoPlay] = useState(false);

  const listRestartHandledRef = useRef(false);

  // --- 인터스티셜 광고: 점진적 간격 (10 → 15 → 20 → 25 → 30 고정)
  const AD_FIRST_INTERVAL = 10;
  const AD_INTERVAL_STEP = 5;
  const AD_MAX_INTERVAL = 30;
  const nextAdSwipeCountRef = useRef(AD_FIRST_INTERVAL);
  const adIntervalRef = useRef(AD_FIRST_INTERVAL);

  /** 쿼리/세트 전환·백그라운드 갱신 시 서버 스냅샷과 덱을 맞춘다(페인트 전 동기화로 깜빡임 최소화). */
  useLayoutEffect(() => {
    if (sourceWords === undefined) {
      return;
    }

    /* eslint-disable react-hooks/set-state-in-effect -- Query 스냅샷을 로컬 세션 상태로 반영(외부 데이터 동기화) */
    const sessionIsFresh = currentIndex === 0 && history.length === 0;
    const queryPayloadChanged =
      sourceDataUpdatedAt != null && syncedDataUpdatedAt != null && sourceDataUpdatedAt !== syncedDataUpdatedAt;
    const shouldSync = syncedSourceKey !== sourceKey || (sessionIsFresh && queryPayloadChanged);

    if (!shouldSync) {
      return;
    }

    const chunk = splitFlashcardSession(sourceWords, settings.sessionSize);

    trackEvent("flashcard_session_started", {
      word_set_id: id,
      total_cards: chunk.sessionWords.length,
      available_cards: sourceWords.length,
      session_size: settings.sessionSize,
      is_favorites: isFavoritesMode,
      is_learning_deck: !isFavoritesMode && Boolean(flashcardData?.isLearningReviewDeck),
    });

    setSyncedSourceKey(sourceKey);
    setSyncedDataUpdatedAt(sourceDataUpdatedAt ?? 0);
    setInitialWords(chunk.sessionWords);
    setAllWords(isFavoritesMode ? sourceWords : (flashcardData?.allWords ?? sourceWords));
    setWords(chunk.sessionWords);
    setRemainingWords(chunk.remainingWords);
    setActiveSessionSize(settings.sessionSize);
    setTotalWordCount(isFavoritesMode ? sourceWords.length : (flashcardData?.totalCount ?? sourceWords.length));
    setBaseKnownCount(flashcardData?.knownCount ?? 0);
    setBaseLearningCount(flashcardData?.learningCount ?? 0);
    setLearningDeckMode(!isFavoritesMode && Boolean(flashcardData?.isLearningReviewDeck));
    setCurrentIndex(0);
    setHistory([]);
    setIsFlipped(false);
    setSwipeDirection(null);
    nextAdSwipeCountRef.current = AD_FIRST_INTERVAL;
    adIntervalRef.current = AD_FIRST_INTERVAL;
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [
    sourceKey,
    sourceWords,
    sourceDataUpdatedAt,
    syncedSourceKey,
    syncedDataUpdatedAt,
    currentIndex,
    history.length,
    isFavoritesMode,
    flashcardData?.allWords,
    flashcardData?.totalCount,
    flashcardData?.knownCount,
    flashcardData?.learningCount,
    flashcardData?.isLearningReviewDeck,
    settings.sessionSize,
  ]);

  const {
    sessionKnownCount,
    sessionLearningCount,
    learningWords,
    headerProgressNumerator,
    headerProgressDenominator,
    knownCount,
    learningCount,
  } = useMemo(() => {
    const summary = summarizeSessionHistory(history);
    const header = computeHeaderProgress({
      totalWordCount,
      deckSize: words.length,
      sessionClassifiedCount: history.length,
      baseKnownCount,
      baseLearningCount,
      learningDeckMode,
      chunkedSession: activeSessionSize !== "all",
    });
    const badges = computeBadgeCounts({
      learningDeckMode,
      baseKnownCount,
      baseLearningCount,
      totalWordCount,
      sessionKnownCount: summary.sessionKnownCount,
      sessionLearningCount: summary.sessionLearningCount,
    });

    return {
      sessionKnownCount: summary.sessionKnownCount,
      sessionLearningCount: summary.sessionLearningCount,
      learningWords: summary.learningWords,
      headerProgressNumerator: header.numerator,
      headerProgressDenominator: header.denominator,
      knownCount: badges.known,
      learningCount: badges.learning,
    };
  }, [history, totalWordCount, words.length, baseKnownCount, baseLearningCount, learningDeckMode, activeSessionSize]);

  const currentWord = words[currentIndex] ?? null;

  const restartAll = useCallback(async () => {
    trackEvent("flashcard_restart", { word_set_id: id, mode: "all" });
    setBaseKnownCount(0);
    setBaseLearningCount(0);
    setLearningDeckMode(false);
    setCurrentIndex(0);
    setHistory([]);
    setIsFlipped(false);
    setSwipeDirection(null);
    nextAdSwipeCountRef.current = AD_FIRST_INTERVAL;
    adIntervalRef.current = AD_FIRST_INTERVAL;

    if (isFavoritesMode) {
      // 즐겨찾기 모드: 해제된 단어가 빠진 최신 목록으로 갱신
      await queryClient.invalidateQueries({ queryKey: ["words", "favorites"] });
    } else if (userId) {
      try {
        await deleteWordProgressBySet(userId, id);
        queryClient.invalidateQueries({ queryKey: ["flashcardWords"] });
      } catch (e) {
        logger.error({ domain: "flashcard", message: "진행도 초기화 실패", error: e, data: { wordSetId: id } });
        toast.error("진행도 초기화에 실패했습니다.");
      }
    }

    // invalidate 후 sourceWords가 갱신되면 useLayoutEffect에서 덱을 동기화하므로
    // 즐겨찾기가 아닌 경우에만 로컬 덱을 직접 세팅
    if (!isFavoritesMode) {
      const chunk = splitFlashcardSession(allWords, settings.sessionSize);
      setWords(chunk.sessionWords);
      setInitialWords(chunk.sessionWords);
      setRemainingWords(chunk.remainingWords);
      setActiveSessionSize(settings.sessionSize);
    }

    if (openOptionsModal || openResultModal || openCompletedModal) {
      popStep();
    } else {
      setTimeout(() => replace("/flashcards/:id", { id }, { transitionName: "none" }), 500);
    }
  }, [
    allWords,
    id,
    isFavoritesMode,
    openOptionsModal,
    openResultModal,
    openCompletedModal,
    popStep,
    queryClient,
    userId,
    replace,
    settings.sessionSize,
  ]);

  function swipe(dir: Direction) {
    const word = words[currentIndex];

    if (!word) {
      return;
    }

    const category =
      settings.trackCategories && dir === Direction.LEFT ? FlashcardCategory.LEARNING : FlashcardCategory.KNOWN;

    setHistory(prev => [...prev, { word, category }]);
    setCurrentIndex(i => i + 1);
    setIsFlipped(false);
    setSwipeDirection(null);

    trackEvent("flashcard_swipe", {
      word_set_id: id,
      category,
      card_index: currentIndex,
      is_favorites: isFavoritesMode,
    });

    if (!isFavoritesMode && userId) {
      upsertWordProgress(userId, word.id, category).catch(() => {
        toast.error("학습 진행도 저장에 실패했습니다.");
      });
    }

    // 점진적 인터스티셜 광고 (마지막 카드 제외 — 결과 모달과 겹치지 않도록)
    // 간격: 10 → 15 → 20 → 25 → 30(고정). 3분 쿨다운과 이중 적용.
    const swipeCount = history.length + 1; // 현재 스와이프 포함

    if (swipeCount >= nextAdSwipeCountRef.current && currentIndex + 1 < words.length) {
      const nextInterval = Math.min(adIntervalRef.current + AD_INTERVAL_STEP, AD_MAX_INTERVAL);
      nextAdSwipeCountRef.current = swipeCount + nextInterval;
      adIntervalRef.current = nextInterval;
      void maybeShowAd("flashcard_swipe");
    }

    // 마지막 카드인 경우 결과 모달 표시
    if (currentIndex + 1 >= words.length) {
      const updatedHistory = [...history, { word, category }];
      const summary = summarizeSessionHistory(updatedHistory);

      trackEvent("flashcard_session_completed", {
        word_set_id: id,
        total_cards: words.length,
        session_size: activeSessionSize,
        has_more_cards: remainingWords.length > 0,
        known_count: summary.sessionKnownCount,
        learning_count: summary.sessionLearningCount,
        is_favorites: isFavoritesMode,
      });

      pushStep({ id, openResultModal: true });
    }
  }

  function undo() {
    if (history.length === 0) {
      return;
    }

    const last = history[history.length - 1];
    setHistory(prev => prev.slice(0, -1));
    setCurrentIndex(i => Math.max(0, i - 1));
    setIsFlipped(false);
    setSwipeDirection(null);

    trackEvent("flashcard_undo", { word_set_id: id, card_index: currentIndex });

    if (!isFavoritesMode && userId && last) {
      deleteWordProgress(userId, last.word.id).catch(error =>
        logger.error({ domain: "flashcard", message: "단어 진행도 삭제 실패", error, data: { wordId: last.word.id } }),
      );
    }
  }

  const restartWithLearning = useCallback(() => {
    const summary = summarizeSessionHistory(history);
    const next = summary.learningWords;

    trackEvent("flashcard_restart", {
      word_set_id: id,
      mode: "learning_only",
      learning_count: summary.learningWords.length,
    });
    setBaseKnownCount(b => b + summary.sessionKnownCount);
    setBaseLearningCount(b => b + summary.sessionLearningCount);
    setWords(next);
    setRemainingWords([]);
    setInitialWords(next);
    setActiveSessionSize(settings.sessionSize);
    setCurrentIndex(0);
    setHistory([]);
    setIsFlipped(false);
    setSwipeDirection(null);
    setLearningDeckMode(true);
    nextAdSwipeCountRef.current = AD_FIRST_INTERVAL;
    adIntervalRef.current = AD_FIRST_INTERVAL;
    popStep();
  }, [history, id, popStep, settings.sessionSize]);

  const continueWithNextSession = useCallback(() => {
    if (remainingWords.length === 0) {
      return;
    }

    const summary = summarizeSessionHistory(history);
    const chunk = splitFlashcardSession(remainingWords, settings.sessionSize);

    trackEvent("flashcard_session_started", {
      word_set_id: id,
      total_cards: chunk.sessionWords.length,
      available_cards: remainingWords.length,
      session_size: settings.sessionSize,
      is_favorites: isFavoritesMode,
      is_learning_deck: learningDeckMode,
      entry: "continue",
    });

    if (!learningDeckMode) {
      setBaseKnownCount(count => count + summary.sessionKnownCount);
      setBaseLearningCount(count => count + summary.sessionLearningCount);
    }

    setWords(chunk.sessionWords);
    setInitialWords(chunk.sessionWords);
    setRemainingWords(chunk.remainingWords);
    setActiveSessionSize(settings.sessionSize);
    setCurrentIndex(0);
    setHistory([]);
    setIsFlipped(false);
    setSwipeDirection(null);
    nextAdSwipeCountRef.current = AD_FIRST_INTERVAL;
    adIntervalRef.current = AD_FIRST_INTERVAL;
    popStep();
  }, [history, id, isFavoritesMode, learningDeckMode, popStep, remainingWords, settings.sessionSize]);

  const updateSettings = useCallback(
    (patch: Partial<FlashcardSettings>) => {
      const next: Partial<FlashcardSettings> = { ...patch };
      if (next.frontSide === Language.KOREAN) {
        next.ttsEnabled = false;
      }

      trackEvent("flashcard_settings_changed", {
        word_set_id: id,
        changed_keys: Object.keys(patch),
        ...patch,
      });

      setSettings(prev => ({ ...prev, ...next }));

      if (next.sessionSize !== undefined) {
        setPersistedSessionSize(next.sessionSize);
      }

      if (next.frontSide !== undefined) {
        setIsFlipped(false);
      }

      if (next.shuffleCards !== undefined) {
        setWords(prev => {
          const viewed = prev.slice(0, currentIndex);
          const viewedIds = new Set(history.map(h => h.word.id));
          if (next.shuffleCards) {
            const remaining = prev.slice(currentIndex);
            return [...viewed, ...shuffleArray(remaining)];
          }
          const remainingOriginalOrder = (initialWords ?? []).filter(w => !viewedIds.has(w.id));
          return [...viewed, ...remainingOriginalOrder];
        });
      }
    },
    [currentIndex, history, id, initialWords, setPersistedSessionSize],
  );

  useAutoPlay(isAutoPlay, isFlipped, currentIndex, words.length, flipped => {
    if (!flipped) {
      setIsFlipped(true);
    } else {
      swipe(Direction.RIGHT);
    }
  });

  /** 덱이 비어있는 경우 빈 완료 모달 표시 */
  useEffect(() => {
    if (openCompletedModal) {
      return;
    }
    if (
      !isPending &&
      syncedSourceKey === sourceKey &&
      sourceWords !== undefined &&
      words.length === 0 &&
      totalWordCount > 0 &&
      !restartFromScratch
    ) {
      pushStep({ id, openCompletedModal: true });
    }
  }, [
    isPending,
    sourceKey,
    sourceWords,
    words.length,
    totalWordCount,
    restartFromScratch,
    syncedSourceKey,
    id,
    pushStep,
    openCompletedModal,
  ]);

  /** 리스트에서「처음부터 다시」진입 시 진행도 초기화 후 쿼리스트립만 제거 */
  useEffect(() => {
    if (!restartFromScratch || !id || listRestartHandledRef.current || isPending) {
      return;
    }

    // 동기화 전 local allWords는 []일 수 있음. 그때 빈 덱으로 처리하면 ref가 고정되어 재실행이 막힘
    if (syncedSourceKey !== sourceKey || sourceWords === undefined) {
      return;
    }

    listRestartHandledRef.current = true;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    restartAll();
  }, [
    restartFromScratch,
    isPending,
    syncedSourceKey,
    sourceKey,
    sourceWords,
    isFavoritesMode,
    flashcardData?.allWords,
    id,
    replace,
    restartAll,
  ]);

  /** 리스트 재시작 처리 완료 표시 */
  useEffect(() => {
    if (restartFromScratch) {
      return;
    }

    listRestartHandledRef.current = false;
  }, [restartFromScratch]);

  /** TTS 활성화 시 현재 단어 읽기 */
  useEffect(() => {
    if (openOptionsModal || !settings.ttsEnabled || settings.frontSide !== Language.ENGLISH || !currentWord) {
      return;
    }

    speak(currentWord.word);
  }, [openOptionsModal, settings.ttsEnabled, settings.frontSide, currentWord?.id, currentWord?.word, speak]);

  const nextSessionCount =
    remainingWords.length === 0
      ? 0
      : settings.sessionSize === "all"
        ? remainingWords.length
        : Math.min(settings.sessionSize, remainingWords.length);

  return {
    isLoading: isPending,
    isError,
    words,
    isFavoritesMode,
    totalWordCount,

    currentIndex,
    currentWord,
    isFlipped,
    isAutoPlay,
    swipeDirection,
    settings,

    sessionKnownCount,
    sessionLearningCount,
    learningWords,
    nextSessionCount,
    headerProgressNumerator,
    headerProgressDenominator,
    knownCount,
    learningCount,

    flip: () => setIsFlipped(v => !v),
    swipe,
    undo,
    toggleAutoPlay: () => setIsAutoPlay(v => !v),
    setSwipeDirection,
    speakWord: (text: string) => speak(text),
    restartWithLearning,
    continueWithNextSession,
    restartAll,
    updateSettings,
  };
}

export type UseFlashcardReturn = ReturnType<typeof useFlashcard>;
